import { useContext, useEffect, useState, createContext } from "react";
import { PartnerType } from "../utils/partners.enum";
import { CreditPartner } from "@/view/pages/Credit/useCreditController";
import { Table } from "../entities/TableCredit";
import { LocationState } from "@/view/pages/PanSimulation/usePanSimulation";
import { OperacoesCredito } from "./PanContext";
import { GetPublicUserResponse } from "../services/selfHireService/getPublicUser";
import { GetOffersProposalResponse } from "../services/crefazService/getOffersProposal";

export type CreditAuthorizationType = Exclude<
  PartnerType,
  "CREFAZ" | "SALVCAR" | "CREFISA"
>;

export interface ProposalData {
  propostaId: string;
  tabelaJurosId?: number;
  prestacao?: number;
  prazo?: number;
  diaRecebimento?: number;
  convenioId?: number;
  value?: number;
}

export interface FgtsData {
  leadId?: string;
  table?: Table | null;
}

export interface CrefazData {
  transactionId: string | null;
  proposalData: ProposalData;
  offersData?: GetOffersProposalResponse;
}

export interface PanData {
  state: LocationState;
}

export interface CltTable {
  Prazo: number;
  tableCode: string;
  ValorSolicitado: number;
  ValorFinanciado: number;
  ValorParcela: number;
  ValorIOC?: number;
  ValorSeguro?: number;
  ValorTC?: number;
  ValorBruto?: number;
  ValorLiquidoCliente?: number;
  DataPrimeiroVencimento?: string;
  DataUltimoVencimento?: string;
  CodigoTabela?: string;
  CodigoProduto?: number;
  CodigoOrgao?: number;
  CodigoEmpregador?: number;
  Taxa?: number;
  [key: string]: unknown;
}

export interface CltData {
  matricula?: string;
  cnpjEmpresa?: string;
  prazos?: number[];
  tables?: CltTable[];
  selectedPrazo?: number;
}

interface SelfHiringContextType {
  step: number;
  formData: any;
  cleanFormData: () => void;
  nextStep: () => void;
  prevStep: (n?: number) => void;
  type: CreditPartner | null;
  setType: (type: CreditPartner) => void;

  step2mode: IStep2Mode;
  setStep2mode: (mode: IStep2Mode) => void;

  fgtsData: FgtsData | null;
  setFgtsData: (data?: any) => void;

  crefazData: CrefazData | null;
  setCrefazData: (data: any) => void;

  panData?: {
    state: LocationState;
    proposal: {
      operacoes_credito: OperacoesCredito;
      customerId: string;
    };
  };
  setPanData: (data?: any) => void;

  cltData: CltData | null;
  setCltData: (data: CltData | null | ((prev: CltData | null) => CltData | null)) => void;

  seller: GetPublicUserResponse | null;
  setSeller: (seller: GetPublicUserResponse | null) => void;
}

export type IStep2Mode =
  | "partner"
  | "installments"
  | "selectValues"
  | "documents";

const SelfHiringContext = createContext<SelfHiringContextType>(
  {} as SelfHiringContextType,
);

export const SelfHiringProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [type, setType] = useState<CreditPartner | null>(null);
  const [step2mode, setStep2mode] = useState<IStep2Mode>("partner");

  const [fgtsData, setFgtsData] = useState<FgtsData | null>(null);
  const [crefazData, setCrefazData] = useState<CrefazData | null>(null);
  const [panData, setPanData] = useState<any | undefined>(undefined);
  const [cltData, setCltData] = useState<CltData | null>(null);

  const [seller, setSeller] = useState<GetPublicUserResponse | null>(null);

  const cleanProductsData = () => {
    setFgtsData(null);
    setCrefazData(null);
    setPanData(undefined);
    setCltData(null);
  };

  const cleanFormData = () => {
    setFormData({});
    setType(null);
    setStep(1);
    setStep2mode("partner");

    cleanProductsData();
  };

  const nextStep = () => {
    if (step + 1 == 2) {
      setStep2mode("partner");
    }
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (type === PartnerType.CREFAZ) {
      setStep2mode("documents");
    }
    if (step > 1) {
      setStep(step - 1);
    }
  };

  useEffect(() => {
    cleanProductsData();
  }, [type]);

  return (
    <SelfHiringContext.Provider
      value={{
        seller,
        setSeller,
        step,
        formData,
        cleanFormData,
        nextStep,
        prevStep,
        type,
        setType,
        step2mode,
        setStep2mode,
        fgtsData,
        setFgtsData,
        crefazData,
        setCrefazData,
        panData,
        setPanData,
        cltData,
        setCltData,
      }}
    >
      {children}
    </SelfHiringContext.Provider>
  );
};

export const useSelfHiringContext = () => {
  return useContext(SelfHiringContext);
};
